import React from 'react';
import { FiTrendingUp } from 'react-icons/fi';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAgentHealthTrends } from '../../hooks/useAgentAnalytics';
import type {
  AnalyticsPeriod,
  HealthTrendPoint,
} from '../../services/agentAnalyticsApi';

interface HealthTimelineChartProps {
  agentId: string;
  period: AnalyticsPeriod;
}

/** Converte YYYY-MM-DD em "DD/MM" para renderização compacta. */
function formatDateLabel(date: string): string {
  const parts = date.split('-');
  if (parts.length !== 3) return date;
  return `${parts[2]}/${parts[1]}`;
}

/** Converte score 0-1 em % inteiro pra eixo Y. */
function toPercent(value: number | null): number | null {
  if (value === null) return null;
  return Math.round(value * 100);
}

interface ChartDataPoint {
  date: string;
  label: string;
  score: number | null;
  traceCount: number;
}

interface TooltipPayloadItem {
  payload?: ChartDataPoint;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div
      className="rounded-md px-3 py-2 text-xs shadow-md"
      style={{
        backgroundColor: '#ffffff',
        color: '#1e293b',
        border: '1px solid rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="font-semibold">{point.date}</div>
      {point.score !== null ? (
        <>
          <div>Score: {point.score}%</div>
          <div style={{ color: '#64748b' }}>
            {point.traceCount}{' '}
            {point.traceCount === 1 ? 'conversa' : 'conversas'}
          </div>
        </>
      ) : (
        <div style={{ color: '#64748b' }}>Sem dados</div>
      )}
    </div>
  );
};

export const HealthTimelineChart: React.FC<HealthTimelineChartProps> = ({
  agentId,
  period,
}) => {
  const { data, isLoading } = useAgentHealthTrends(agentId, period);

  const chartData: ChartDataPoint[] = (data?.points ?? []).map(
    (p: HealthTrendPoint) => ({
      date: p.date,
      label: formatDateLabel(p.date),
      score: toPercent(p.overallScore),
      traceCount: p.traceCount,
    }),
  );

  return (
    <div
      className="rounded-lg border p-5"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <FiTrendingUp
          className="h-5 w-5"
          style={{ color: 'var(--theme-primary)' }}
        />
        <h3
          className="text-base font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Evolução da saúde do agente
        </h3>
      </div>

      {isLoading && (
        <div
          className="text-sm text-center py-10"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Carregando histórico...
        </div>
      )}

      {!isLoading && data && !data.hasEvaluators && (
        <div
          className="text-sm text-center py-10"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Sem dados suficientes para gerar a linha do tempo. Execute mais
          conversas com este agente.
        </div>
      )}

      {!isLoading && data && data.hasEvaluators && chartData.length > 0 && (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--theme-card-border)"
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }}
                stroke="var(--theme-card-border)"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }}
                stroke="var(--theme-card-border)"
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={{ r: 4, fill: '#8b5cf6' }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
